import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ReactNode } from 'react';
import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyM, HeadlineM, LabelM } from '@sorare/core/src/atoms/typography';
import { Avatar } from '@sorare/core/src/components/user/Avatar';
import { MyClubLink } from '@sorare/core/src/components/user/MyClubLink';
import { UserName } from '@sorare/core/src/components/user/UserName';
import {
  ANY_SPORT_PLAY,
  ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_CONTENDER_CONTENDER,
} from '@sorare/core/src/constants/__generated__/routes';
import { useSeoContext } from '@sorare/core/src/contexts/seo';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { glossary } from '@sorare/core/src/lib/glossary';

import { LineupDate } from 'components/lineup/LineupDate';
import { getLineupDisplayName } from 'lib/lineup/getLineupDisplayName';

import type { LineupSharing_so5Lineup } from './__generated__/index.graphql';

const Root = styled(Vertical).attrs({ center: true })<{ $field: string }>`
  height: var(--100vh);
  padding: var(--double-unit);
  background-image: url(${({ $field }) => $field});
  background-repeat: no-repeat;
  background-position: center 0%;
  background-color: var(--c-nd-50);
`;
const Header = styled.div`
  display: grid;
  grid-template-areas:
    'logo name'
    'logo date';
  grid-template-columns: min-content 1fr;
  column-gap: var(--unit);
  align-items: center;
`;
const Logo = styled.img`
  grid-area: logo;
  width: 40px;
  height: 40px;
  justify-self: flex-end;
`;
const LeaderboardName = styled(HeadlineM)`
  grid-area: name;
  text-align: left;
`;
const Date = styled.div`
  grid-area: date;
  text-align: left;
`;
const StyledMyClubLink = styled(MyClubLink)`
  background-color: rgba(var(--c-rgb-white), 0.1);
  border-radius: var(--double-unit);
  padding: 2px var(--unit);
`;

type Props = {
  so5Lineup: LineupSharing_so5Lineup;
  field: string;
  so5LineupNode: ReactNode;
};

export const LineupSharing = ({ so5Lineup, field, so5LineupNode }: Props) => {
  const { setPageMetadata } = useSeoContext();
  const { generateSportContextPath } = useSportContext();
  useEffect(() => {
    if (so5Lineup?.so5Leaderboard) {
      setPageMetadata(
        `${getLineupDisplayName({
          so5Leaderboard: so5Lineup.so5Leaderboard,
          so5Lineup,
          managerTeam: so5Lineup.managerTeam,
          addLeaguePrefix: true,
        })}`,
        {
          img: so5Lineup?.socialPictureUrls.post,
        }
      );
    }
  }, [setPageMetadata, so5Lineup]);

  if (!so5Lineup) return null;

  const { so5Leaderboard, so5LeaderboardContender, so5Fixture, user } =
    so5Lineup;

  return (
    <Root gap={3} $field={field}>
      <Vertical center>
        <Header>
          <Logo src={so5Leaderboard?.svgLogoUrl} />
          <Date>
            <LineupDate fixture={so5Fixture} />
          </Date>
          <LeaderboardName as="h4">
            {so5Leaderboard?.so5LeaderboardGroup.displayName}
            {' - '}
            {so5Leaderboard?.displayName}
          </LeaderboardName>
        </Header>
        <StyledMyClubLink user={user}>
          <Horizontal as="span">
            <Avatar user={user} size={2} rounded />
            <LabelM>
              <UserName user={user} />
            </LabelM>
          </Horizontal>
        </StyledMyClubLink>
      </Vertical>
      {so5LineupNode}

      <Vertical center>
        <Button
          to={generateSportContextPath(
            ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_CONTENDER_CONTENDER,
            {
              params: {
                eventType: so5Fixture.type.toLowerCase(),
                fixture: so5Fixture.slug,
                contender: so5LeaderboardContender.slug,
              },
            }
          )}
          color="primary"
          size="medium"
        >
          <FormattedMessage {...glossary.viewDetails} />
        </Button>
        <Link to={generateSportContextPath(ANY_SPORT_PLAY)}>
          <BodyM bold color="var(--c-white)">
            <FormattedMessage
              id="LineupSharing.Cta.goBackHome"
              defaultMessage="Go back to home"
            />
          </BodyM>
        </Link>
      </Vertical>
    </Root>
  );
};

LineupSharing.fragments = {
  so5Lineup: gql`
    fragment LineupSharing_so5Lineup on So5Lineup {
      id
      so5LeaderboardContender {
        slug
      }
      so5Leaderboard {
        slug
        mainRarityType
        so5LeaderboardGroup {
          slug
          iconUrl
          displayName
        }
        displayName
        svgLogoUrl
        ...getLineupDisplayName_so5Leaderboard
      }
      managerTeam {
        id
        ...getLineupDisplayName_managerTeam
      }
      so5Fixture {
        slug
        type
        startDate
        ...LineupDate_fixture
      }
      socialPictureUrls {
        post
        square
        story
      }
      user {
        slug
        ...UserName_publicUserInfoInterface
        ...Avatar_publicUserInfoInterface
        ...MyClubLink_publicUserInfoInterface
      }
      ...getLineupDisplayName_so5Lineup
    }
    ${LineupDate.fragments.fixture}
    ${MyClubLink.fragments.user}
    ${UserName.fragments.user}
    ${Avatar.fragments.publicUserInfoInterface}
    ${getLineupDisplayName.fragments.so5Leaderboard}
    ${getLineupDisplayName.fragments.so5Lineup}
    ${getLineupDisplayName.fragments.managerTeam}
  ` as TypedDocumentNode<LineupSharing_so5Lineup>,
};
